import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Setter = (node: ReactNode | undefined) => void;
export type HeaderTone = "default" | "article";
const Ctx = createContext<Setter | null>(null);
const ToneCtx = createContext<((tone: HeaderTone) => void) | null>(null);

/** Lets a page under the public layout replace the header's end actions and ground tone. */
export function HeaderEndSlotProvider({
  children,
}: {
  children: (slot: ReactNode | undefined, tone: HeaderTone) => ReactNode;
}) {
  const [slot, setSlot] = useState<ReactNode | undefined>(undefined);
  const [tone, setTone] = useState<HeaderTone>("default");
  return (
    <Ctx.Provider value={setSlot}>
      <ToneCtx.Provider value={setTone}>{children(slot, tone)}</ToneCtx.Provider>
    </Ctx.Provider>
  );
}

/**
 * Set the header end slot while the calling page is mounted. `key` must change
 * whenever the node's content changes (the node itself is rebuilt every render).
 */
export function useHeaderEndSlot(node: ReactNode | undefined, key: string) {
  const set = useContext(Ctx);
  useEffect(() => {
    set?.(node);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set, key]);
  useEffect(() => () => set?.(undefined), [set]);
}

/** Set the header ground tone while the calling page is mounted. */
export function useHeaderTone(tone: HeaderTone) {
  const set = useContext(ToneCtx);
  useEffect(() => {
    set?.(tone);
    return () => set?.("default");
  }, [set, tone]);
}
